import axios from 'axios';
import { getCurrentToken, getRefreshToken, isTokenExpired, setAccessToken, logout } from './auth';

// Use environment variables or fallback to hardcoded values for API URL
// const API_URL = process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:5000/api`;
const BASE_PHOTO_URL = "https://api.myweeshy.com";
const API_URL = BASE_PHOTO_URL + "/api";
// const API_URL = "http://localhost:5000/api";
const WEB_URL = "https://myweeshy.com";

// Create axios instance
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

api.interceptors.request.use(
    async (config) => {
        if (config.headers?.['X-Requires-Auth']) {
            let token = getCurrentToken();

            if (token && isTokenExpired(token)) {
                try {
                    token = await refreshAccessToken();
                    if (!token) {
                        logout();
                        return Promise.reject("logout");
                    }
                } catch (error) {
                    console.error('Token refresh failed:', error);
                    // logout();
                    return Promise.reject(error);
                }
            }

            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }

        return config;
    },
    (error) => Promise.reject(error)
);

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If token expired, refresh it and retry request
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true; // Prevent infinite retry loops

            try {
                const newToken = await refreshAccessToken();
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return api(originalRequest);
            } catch (refreshError) {
                logout();
                return Promise.reject(refreshError);
            }
        }
        return Promise.reject(error);
    }
);

export const applyReferral = (referralCode: string) => {
    return axios.post('/api/refer/apply', { referral_code: referralCode });
};

export const premiumSubscribed = () => {
    return axios.post('/api/refer/premium-subscribed');
};

export const getMyReferrals = () => {
    return api.get('/refer/my-referrals');
};

// Refresh Access Token Logic
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (token) prom.resolve(token);
        else prom.reject(error);
    });
    failedQueue = [];
};

const refreshAccessToken = async (): Promise<string> => {
    if (isRefreshing) {
        return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
        });
    }

    isRefreshing = true;
    const refreshToken = getRefreshToken();
    if (!refreshToken || refreshToken === 'undefined') throw new Error('No refresh token available');

    try {
        const response = await axios.post(`${API_URL}/auth/refresh`, { refresh_token: refreshToken });
        console.log(response);
        const accessToken = response?.data?.access_token;

        console.log("accessToken", accessToken);


        setAccessToken(accessToken); // Update access token in storage
        processQueue(null, accessToken);
        return accessToken;
    } catch (error) {
        processQueue(error);
        throw new Error('Failed to refresh token');
    } finally {
        isRefreshing = false;
    }
};

// API functions for authentication and user management
export const login = (credentials: { email: string; password: string }) => api.post('/auth/login', credentials);
export const signup = (userData: { email: string; password: string; name: string; username: string; [key: string]: any }) =>
    api.post('/auth/signup', userData);
export const refreshToken = (refreshToken: string) => api.post('/auth/refresh', { refresh_token: refreshToken });
// export const logout = () => api.post('/auth/logout', null, { headers: { 'X-Requires-Auth': 'true' } });
export const getUserProfile = () => api.get('/users/profile', { headers: { 'X-Requires-Auth': 'true' } });
export const updateUserProfile = async (formData: FormData) => {
    return api.put('/users/profile', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};
export const updateUserProfileSocialLinks = (profileData: any) => api.put('/users/profile', profileData, { headers: { 'X-Requires-Auth': 'true' } });
export const getUserByUsername = (username: string) => api.get(`/users/${username}/public-profile`);
export const requestVerification = () => api.post('/users/verify', null, { headers: { 'X-Requires-Auth': 'true' } });
export const getEvents = (activeOnly: boolean = false) => api.get('/events', { params: { active: activeOnly }, headers: { 'X-Requires-Auth': 'true' } });
export const getEvent = (eventId: string) => api.get(`/events/${eventId}`, { headers: { 'X-Requires-Auth': 'true' } });
export const createEvent = (eventData: any) => api.post('/events', eventData, { headers: { 'X-Requires-Auth': 'true' } });
export const updateEvent = (eventId: string, eventData: any) => api.put(`/events/${eventId}`, eventData, { headers: { 'X-Requires-Auth': 'true' } });
export const deleteEvent = (eventId: string) => api.delete(`/events/${eventId}`, { headers: { 'X-Requires-Auth': 'true' } });
export const getPublicEvents = () => api.get('/events/public');
export const getDjEvents = (userId: string) => api.get(`/events?dj_id=${userId}`);
export const getSongRequests = (eventId: string, status?: string) => {
    const params: any = {};
    if (status) params.status = status;
    return api.get(`/events/${eventId}/requests`, { params });
};

export const getSongById = (eventId: string) => {
    const params: any = {};
    return api.get(`/requests/events/${eventId}/requests`, { params });
};
export const createSongRequest = (eventId: string, requestData: any) =>
    api.post(`/requests/events/${eventId}/requests`, requestData, { headers: { 'X-Requires-Auth': 'true' } });
export const getPublicRequests = (eventId: string) => api.get(`/requests/events/${eventId}/public-requests`);
export const createPayment = (paymentData: any) => api.post('/payments/create', paymentData, { headers: { 'X-Requires-Auth': 'true' } });
export const getPaymentHistory = () => api.get('/payments/history', { headers: { 'X-Requires-Auth': 'true' } });

export const getWebUrl = () => {
    return WEB_URL;
};

export const getBasePhotoUrl = () => {
    return BASE_PHOTO_URL;
}

// Subscription plans
export const getSubscriptionPlans = () => {
    return Promise.resolve({
        data: {
            plans: [
                {
                    id: 'free',
                    name: 'Free',
                    price: 0,
                    currency: '€',
                    interval: 'month',
                    features: [
                        'Up to 7 events per month',
                        'Basic analytics',
                        'Standard support'
                    ],
                    eventsPerMonth: 7,
                    highlighted: false
                },
                {
                    id: 'premium',
                    name: 'Premium',
                    price: 9.99,
                    currency: '€',
                    interval: 'month',
                    features: [
                        'Unlimited events',
                        'Advanced analytics',
                        'Priority support',
                        'Verified badge',
                        'Custom event branding'
                    ],
                    eventsPerMonth: 'unlimited',
                    highlighted: true
                }
            ]
        }
    });
};

export default api;
